import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import ImavenLoadingScreen from '../../../Components/Helpers/loading-screen';
import DOMPurify from 'dompurify';
import { useToast } from '../../../Context/ToastContext';
import { useConfirm } from '../../../Context/ConfirmContext';

// ============================================
// SVG ICONS
// ============================================

const Icons = {
  brain: <div style={{ 
    width: 80, 
    height: 80, 
    borderRadius: 16, 
    background: 'linear-gradient(135deg, rgba(10, 14, 26, 0.98) 0%, rgba(14, 18, 32, 0.95) 100%)', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  }}>
    <img src={require('../../../Images/brain.gif')} alt="Brain" style={{ width: 240, height: 240, objectFit: 'contain' }} />
  </div>,
  play: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  pause: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  x: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  message: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  star: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  starFilled: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  lightbulb: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>,
  book: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  send: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  edit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>,
  loader: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  history: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  help: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
};

// ============================================
// BRAIN CONTEXT HELP TOOLTIPS
// ============================================

const BRAIN_CONTEXT_HELP = {
  widget: "Interactive Q&A with a client - simulates the actual widget chat experience where identification happens first, then Perfect Brain takes over.",
  case_study: "You give it all the info upfront, it analyzes deeply and provides detailed recommendations.",
  observer: "You show it a conversation, it tells you what was missed or could be improved.",
  summarizer: "You give it data, it gives you a clean, concise summary.",
  document_review: "You give it documents, it checks them for completeness and accuracy."
};

// ============================================
// ANIMATIONS
// ============================================

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const ModuleContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(15, 20, 35, 0.98) 0%, rgba(20, 25, 45, 0.95) 100%);
`;

const Header = styled.div`
  padding: 24px 32px;
  border-bottom: 1px solid rgba(236, 72, 153, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    background: linear-gradient(135deg, #f472b6 0%, #ec4899 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  svg {
    color: #ec4899;
    width: 24px;
    height: 24px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => props.primary 
    ? 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' 
    : 'rgba(236, 72, 153, 0.15)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(236, 72, 153, 0.3)'};
  border-radius: 8px;
  color: ${props => props.primary ? '#fff' : '#f472b6'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.primary 
      ? 'linear-gradient(135deg, #db2777 0%, #be185d 100%)' 
      : 'rgba(236, 72, 153, 0.25)'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  ${props => props.loading && css`
    svg {
      animation: ${spin} 1s linear infinite;
    }
  `}
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
  display: flex;
  gap: 24px;
  
  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(236, 72, 153, 0.3) transparent;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(236, 72, 153, 0.3);
    border-radius: 3px;
  }
`;

const Sidebar = styled.div`
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled.div`
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid rgba(236, 72, 153, 0.2);
  border-radius: 16px;
  padding: 20px;
  animation: ${slideIn} 0.3s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;
`;

const CardTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #f472b6;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    opacity: 0.7;
  }
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
`;

const SessionItem = styled.div`
  position: relative;
  padding: 12px;
  padding-right: 36px;
  background: ${props => props.active ? 'rgba(236, 72, 153, 0.2)' : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${props => props.active ? 'rgba(236, 72, 153, 0.4)' : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(236, 72, 153, 0.15);
  }
  
  .name {
    font-size: 13px;
    font-weight: 500;
    color: #fff;
    margin-bottom: 4px;
  }
  
  .meta {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
    display: flex;
    gap: 12px;
  }
  
  .status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
    
    &.in_progress {
      background: rgba(34, 197, 94, 0.2);
      color: #22c55e;
    }
    
    &.completed {
      background: rgba(99, 102, 241, 0.2);
      color: #818cf8;
    }
    
    &.paused {
      background: rgba(251, 191, 36, 0.2);
      color: #fbbf24;
    }
  }
  
  .delete-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    opacity: 0;
    transition: all 0.2s;
    
    &:hover {
      background: rgba(239, 68, 68, 0.2);
      color: #ef4444;
    }
  }
  
  &:hover .delete-btn {
    opacity: 1;
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(25, 30, 50, 0.8);
  border: 1px solid rgba(236, 72, 153, 0.2);
  border-radius: 16px;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(236, 72, 153, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .title {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
  }
  
  .subtitle {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MessageBubble = styled.div`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  animation: ${slideIn} 0.2s ease-out;
  
  ${props => props.isUser ? css`
    align-self: flex-end;
    background: linear-gradient(135deg, #ec4899 0%, #db2777 100%);
    color: #fff;
    border-bottom-right-radius: 4px;
  ` : css`
    align-self: flex-start;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
    border-bottom-left-radius: 4px;
  `}
  
  /* HTML content styling */
  p {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul, ol {
    margin: 8px 0;
    padding-left: 20px;
  }
  
  li {
    margin: 4px 0;
  }
  
  strong, b {
    font-weight: 600;
  }
  
  a {
    color: #f472b6;
    text-decoration: underline;
  }
`;

const TypingIndicator = styled.span`
  animation: ${pulse} 1s infinite;
`;

const EvaluationPanel = styled.div`
  padding: 16px 20px;
  border-top: 1px solid rgba(236, 72, 153, 0.1);
  background: rgba(15, 20, 35, 0.5);
`;

const RatingStars = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  
  .star {
    cursor: pointer;
    color: ${props => props.rating >= props.value ? '#fbbf24' : 'rgba(255, 255, 255, 0.3)'};
    transition: all 0.2s;
    
    &:hover {
      transform: scale(1.2);
    }
  }
`;

const EvaluationCheckboxes = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  
  label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    
    input {
      accent-color: #ec4899;
    }
  }
`;

const CorrectionArea = styled.div`
  margin-top: 12px;
  
  textarea {
    width: 100%;
    padding: 12px;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(236, 72, 153, 0.2);
    border-radius: 8px;
    color: #fff;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    min-height: 80px;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.3);
    }
    
    &:focus {
      outline: none;
      border-color: rgba(236, 72, 153, 0.5);
    }
  }
`;

const ChatInput = styled.div`
  padding: 16px 20px;
  border-top: 1px solid rgba(236, 72, 153, 0.1);
  display: flex;
  gap: 12px;
  
  input {
    flex: 1;
    padding: 12px 16px;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(236, 72, 153, 0.2);
    border-radius: 8px;
    color: #fff;
    font-size: 13px;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.3);
    }
    
    &:focus {
      outline: none;
      border-color: rgba(236, 72, 153, 0.5);
    }
  }
`;

const TeachingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 250px;
  overflow-y: auto;
`;

const TeachingItem = styled.div`
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  
  .name {
    font-size: 13px;
    font-weight: 500;
    color: #fff;
    margin-bottom: 4px;
  }
  
  .description {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 8px;
  }
  
  .actions {
    display: flex;
    gap: 8px;
  }
`;

const SmallButton = styled.button`
  padding: 4px 8px;
  font-size: 11px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'success' ? css`
    background: rgba(34, 197, 94, 0.2);
    border: 1px solid rgba(34, 197, 94, 0.3);
    color: #22c55e;
    
    &:hover {
      background: rgba(34, 197, 94, 0.3);
    }
  ` : props.variant === 'danger' ? css`
    background: rgba(239, 68, 68, 0.2);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.3);
    }
  ` : css`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.7);
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.5);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.3;
  }
  
  p {
    margin: 0;
    font-size: 13px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const StatItem = styled.div`
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  text-align: center;
  
  .value {
    font-size: 24px;
    font-weight: 700;
    color: #f472b6;
    margin-bottom: 4px;
  }
  
  .label {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

// ============================================
// COMPONENT
// ============================================

const TrainingModeModule = forwardRef((props, ref) => {
  const axios = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  // State
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [pendingTeachings, setPendingTeachings] = useState([]);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Evaluation state
  const [currentExchange, setCurrentExchange] = useState(null);
  const [rating, setRating] = useState(3);
  const [wasCorrect, setWasCorrect] = useState(true);
  const [wasComplete, setWasComplete] = useState(true);
  const [wasHelpful, setWasHelpful] = useState(true);
  const [correction, setCorrection] = useState('');
  const [shouldBecomeTeaching, setShouldBecomeTeaching] = useState(false);
  
  // New session modal state
  const [showNewSession, setShowNewSession] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionContext, setNewSessionContext] = useState('widget');
  
  // Expose method to parent component
  useImperativeHandle(ref, () => ({
    openNewSessionModal: () => {
      setShowNewSession(true);
    }
  }));
  
  // Load data
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [sessionsRes, teachingsRes] = await Promise.all([
        axios.get('/api/brain/v2/training/sessions'),
        axios.get('/api/brain/v2/teachings?status=pending')
      ]);
      
      setSessions(sessionsRes.data.data || []);
      setPendingTeachings(teachingsRes.data.data || []);
    } catch (error) {
      console.error('Error loading training data:', error);
    }
    setLoading(false);
  };
  
  const startNewSession = async () => {
    if (!newSessionName.trim()) return;
    
    try {
      const response = await axios.post('/api/brain/v2/training/sessions', {
        name: newSessionName,
        brainContext: newSessionContext,
        description: `Training session for ${newSessionContext}`
      });
      
      const session = response.data.data;
      setSessions(prev => [session, ...prev]);
      setActiveSession(session);
      setShowNewSession(false);
      setNewSessionName('');
      
      // Create a default scenario
      await createScenario(session._id);
    } catch (error) {
      console.error('Error starting session:', error);
    }
  };
  
  const createScenario = async (sessionId) => {
    try {
      const response = await axios.post(`/api/brain/v2/training/sessions/${sessionId}/scenarios`, {
        name: 'Training Scenario',
        description: 'Interactive training scenario',
        brainContext: 'widget'
      });
      
      setCurrentScenario(response.data.data);
      setMessages([]);
    } catch (error) {
      console.error('Error creating scenario:', error);
    }
  };
  
  const deleteSession = async (e, sessionId) => {
    e.stopPropagation(); // Prevent selecting the session
    
    const confirmed = await confirm({
      title: 'Delete Training Session',
      message: 'Are you sure you want to delete this training session? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      await axios.delete(`/api/brain/v2/training/sessions/${sessionId}`);
      
      // Remove from list
      setSessions(prev => prev.filter(s => s._id !== sessionId));
      
      // If this was the active session, clear it
      if (activeSession?._id === sessionId) {
        setActiveSession(null);
        setCurrentScenario(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      showToast('Failed to delete session. Please try again.', 'error');
    }
  };
  
  // Filter to show only in_progress sessions in sidebar
  const activeSessions = sessions.filter(s => s.status === 'in_progress');
  const completedSessions = sessions.filter(s => s.status === 'completed');
  
  const sendMessage = async () => {
    if (!inputMessage.trim() || !activeSession || !currentScenario) return;
    
    const userMessage = inputMessage;
    setInputMessage('');
    
    // Add user message
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setIsTyping(true);
    
    try {
      const response = await axios.post(
        `/api/brain/v2/training/sessions/${activeSession._id}/scenarios/${currentScenario.id}/exchange`,
        { userMessage }
      );
      
      const exchange = response.data.data;
      
      // Add AI response
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: exchange.aiResponse,
        exchangeId: exchange.exchangeId
      }]);
      
      // Set up for evaluation
      setCurrentExchange(exchange);
      setRating(3);
      setWasCorrect(true);
      setWasComplete(true);
      setWasHelpful(true);
      setCorrection('');
      setShouldBecomeTeaching(false);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: 'Error: Could not get AI response. Please try again.'
      }]);
    }
    
    setIsTyping(false);
  };
  
  const submitEvaluation = async () => {
    if (!currentExchange || !activeSession || !currentScenario) return;
    
    try {
      const evaluationData = {
        score: rating,
        wasCorrect,
        wasComplete,
        wasHelpful
      };
      
      if (correction.trim()) {
        evaluationData.correction = {
          correctedResponse: correction,
          reasoning: `Score: ${rating}/5. ${!wasCorrect ? 'Incorrect. ' : ''}${!wasComplete ? 'Incomplete. ' : ''}${!wasHelpful ? 'Not helpful. ' : ''}`,
          shouldBecomeTeaching
        };
      }
      
      await axios.post(
        `/api/brain/v2/training/sessions/${activeSession._id}/scenarios/${currentScenario.id}/exchanges/${currentExchange.exchangeId}/evaluate`,
        evaluationData
      );
      
      // Reset evaluation state
      setCurrentExchange(null);
      setCorrection('');
      setShouldBecomeTeaching(false);
      
      // Refresh teachings if one was created
      if (shouldBecomeTeaching) {
        const teachingsRes = await axios.get('/api/brain/v2/teachings?status=pending');
        setPendingTeachings(teachingsRes.data.data || []);
      }
    } catch (error) {
      console.error('Error submitting evaluation:', error);
    }
  };
  
  const completeSession = async () => {
    if (!activeSession) return;
    
    try {
      await axios.post(`/api/brain/v2/training/sessions/${activeSession._id}/complete`);
      
      // Update session in list
      setSessions(prev => prev.map(s => 
        s._id === activeSession._id ? { ...s, status: 'completed' } : s
      ));
      
      setActiveSession(null);
      setCurrentScenario(null);
      setMessages([]);
    } catch (error) {
      console.error('Error completing session:', error);
    }
  };
  
  const applyTeaching = async (teachingId) => {
    try {
      await axios.post(`/api/brain/v2/teachings/${teachingId}/apply`);
      setPendingTeachings(prev => prev.filter(t => t._id !== teachingId));
    } catch (error) {
      console.error('Error applying teaching:', error);
    }
  };
  
  const rejectTeaching = async (teachingId) => {
    try {
      await axios.post(`/api/brain/v2/teachings/${teachingId}/reject`);
      setPendingTeachings(prev => prev.filter(t => t._id !== teachingId));
    } catch (error) {
      console.error('Error rejecting teaching:', error);
    }
  };
  
  if (loading) {
    return <ImavenLoadingScreen loadingPhrases={['Loading Training Mode...', 'Preparing your AI trainer...']} />;
  }
  
  return (
    <ModuleContainer>
      <Header>
        <HeaderTitle>
          {Icons.brain}
          <h2>Training Mode</h2>
        </HeaderTitle>
      </Header>
      
      <ContentArea>
        <Sidebar>
          {/* Active Sessions List */}
          <Card delay="0s">
            <CardTitle>
              {Icons.play}
              Active Sessions
            </CardTitle>
            <SessionList>
              {activeSessions.length === 0 ? (
                <EmptyState>
                  <p>No active sessions</p>
                  <small style={{ color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                    Click "New Session" to start training
                  </small>
                </EmptyState>
              ) : (
                activeSessions.map(session => (
                  <SessionItem 
                    key={session._id}
                    active={activeSession?._id === session._id}
                    onClick={() => {
                      setActiveSession(session);
                      if (session.scenarios?.length > 0) {
                        setCurrentScenario(session.scenarios[session.scenarios.length - 1]);
                        // Load messages from scenario
                        const msgs = [];
                        session.scenarios[session.scenarios.length - 1].exchanges?.forEach(ex => {
                          msgs.push({ type: 'user', content: ex.userMessage });
                          msgs.push({ type: 'ai', content: ex.aiResponse, exchangeId: ex.id });
                        });
                        setMessages(msgs);
                      } else {
                        createScenario(session._id);
                      }
                    }}
                  >
                    <div className="name">{session.name}</div>
                    <div className="meta">
                      <span className={`status ${session.status}`}>
                        {Icons.play}
                        in progress
                      </span>
                      <span>{session.metrics?.totalExchanges || 0} exchanges</span>
                    </div>
                    <button 
                      className="delete-btn" 
                      onClick={(e) => deleteSession(e, session._id)}
                      title="Delete session"
                    >
                      {Icons.trash}
                    </button>
                  </SessionItem>
                ))
              )}
            </SessionList>
            {completedSessions.length > 0 && (
              <div style={{ 
                marginTop: '12px', 
                padding: '8px 12px', 
                background: 'rgba(99, 102, 241, 0.1)',
                borderRadius: '6px',
                fontSize: '11px',
                color: 'rgba(255,255,255,0.6)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {Icons.history}
                {completedSessions.length} completed session{completedSessions.length > 1 ? 's' : ''}
              </div>
            )}
          </Card>
          
          {/* Pending Teachings */}
          <Card delay="0.1s">
            <CardTitle>
              {Icons.lightbulb}
              Pending Teachings ({pendingTeachings.length})
            </CardTitle>
            <TeachingsList>
              {pendingTeachings.length === 0 ? (
                <EmptyState>
                  <p>No pending teachings</p>
                </EmptyState>
              ) : (
                pendingTeachings.map(teaching => (
                  <TeachingItem key={teaching._id}>
                    <div className="name">{teaching.name}</div>
                    <div className="description">{teaching.description?.slice(0, 80)}...</div>
                    <div className="actions">
                      <SmallButton variant="success" onClick={() => applyTeaching(teaching._id)}>
                        {Icons.check} Apply
                      </SmallButton>
                      <SmallButton variant="danger" onClick={() => rejectTeaching(teaching._id)}>
                        {Icons.x} Reject
                      </SmallButton>
                    </div>
                  </TeachingItem>
                ))
              )}
            </TeachingsList>
          </Card>
          
          {/* Stats */}
          {activeSession && (
            <Card delay="0.2s">
              <CardTitle>
                {Icons.star}
                Session Stats
              </CardTitle>
              <StatsGrid>
                <StatItem>
                  <div className="value">{activeSession.metrics?.totalExchanges || 0}</div>
                  <div className="label">Exchanges</div>
                </StatItem>
                <StatItem>
                  <div className="value">{(activeSession.metrics?.averageScore || 0).toFixed(1)}</div>
                  <div className="label">Avg Score</div>
                </StatItem>
                <StatItem>
                  <div className="value">{activeSession.metrics?.correctResponses || 0}</div>
                  <div className="label">Correct</div>
                </StatItem>
                <StatItem>
                  <div className="value">{activeSession.metrics?.teachingsGenerated || 0}</div>
                  <div className="label">Teachings</div>
                </StatItem>
              </StatsGrid>
            </Card>
          )}
        </Sidebar>
        
        <MainContent>
          {/* Chat Container */}
          <ChatContainer>
            <ChatHeader>
              <div>
                <div className="title">
                  {activeSession ? activeSession.name : 'Start a Training Session'}
                </div>
                <div className="subtitle">
                  {activeSession 
                    ? `Training ${activeSession.trainingFocus?.brainContext || 'widget'} agent`
                    : 'Create a new session to begin training your AI'
                  }
                </div>
              </div>
              {activeSession && activeSession.status === 'in_progress' && (
                <ActionButton onClick={completeSession}>
                  {Icons.check}
                  Complete Session
                </ActionButton>
              )}
            </ChatHeader>
            
            <ChatMessages>
              {messages.length === 0 ? (
                <EmptyState>
                  {Icons.message}
                  <p>Start a conversation to begin training</p>
                </EmptyState>
              ) : (
                messages.map((msg, idx) => (
                  <MessageBubble 
                    key={idx} 
                    isUser={msg.type === 'user'}
                    dangerouslySetInnerHTML={
                      msg.type === 'ai' 
                        ? { __html: DOMPurify.sanitize(msg.content) }
                        : undefined
                    }
                  >
                    {msg.type === 'user' ? msg.content : null}
                  </MessageBubble>
                ))
              )}
              {isTyping && (
                <MessageBubble>
                  <TypingIndicator>AI is thinking...</TypingIndicator>
                </MessageBubble>
              )}
            </ChatMessages>
            
            {/* Evaluation Panel */}
            {currentExchange && (
              <EvaluationPanel>
                <div style={{ marginBottom: '12px', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                  Rate this response:
                </div>
                
                <RatingStars>
                  {[1, 2, 3, 4, 5].map(value => (
                    <span 
                      key={value} 
                      className="star" 
                      onClick={() => setRating(value)}
                      style={{ color: rating >= value ? '#fbbf24' : 'rgba(255,255,255,0.3)' }}
                    >
                      {rating >= value ? Icons.starFilled : Icons.star}
                    </span>
                  ))}
                </RatingStars>
                
                <EvaluationCheckboxes>
                  <label>
                    <input type="checkbox" checked={wasCorrect} onChange={e => setWasCorrect(e.target.checked)} />
                    Correct
                  </label>
                  <label>
                    <input type="checkbox" checked={wasComplete} onChange={e => setWasComplete(e.target.checked)} />
                    Complete
                  </label>
                  <label>
                    <input type="checkbox" checked={wasHelpful} onChange={e => setWasHelpful(e.target.checked)} />
                    Helpful
                  </label>
                </EvaluationCheckboxes>
                
                <CorrectionArea>
                  <textarea 
                    placeholder="Provide a better response (optional)..."
                    value={correction}
                    onChange={e => setCorrection(e.target.value)}
                  />
                  {correction && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '12px', color: '#f472b6' }}>
                      <input 
                        type="checkbox" 
                        checked={shouldBecomeTeaching} 
                        onChange={e => setShouldBecomeTeaching(e.target.checked)} 
                      />
                      Save as Teaching (AI will learn from this)
                    </label>
                  )}
                </CorrectionArea>
                
                <ActionButton 
                  primary 
                  onClick={submitEvaluation}
                  style={{ marginTop: '12px' }}
                >
                  {Icons.check}
                  Submit Evaluation
                </ActionButton>
              </EvaluationPanel>
            )}
            
            {/* Input */}
            {activeSession && activeSession.status === 'in_progress' && (
              <ChatInput>
                <input 
                  type="text"
                  placeholder="Type a simulated user message..."
                  value={inputMessage}
                  onChange={e => setInputMessage(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && sendMessage()}
                  disabled={isTyping}
                />
                <ActionButton primary onClick={sendMessage} disabled={isTyping || !inputMessage.trim()}>
                  {isTyping ? Icons.loader : Icons.send}
                </ActionButton>
              </ChatInput>
            )}
          </ChatContainer>
        </MainContent>
      </ContentArea>
      
      {/* New Session Modal */}
      {showNewSession && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <Card style={{ width: '400px', animation: 'none' }}>
            <CardTitle>Start New Training Session</CardTitle>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                Session Name
              </label>
              <input 
                type="text"
                placeholder="e.g., Work Permit Training"
                value={newSessionName}
                onChange={e => setNewSessionName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0,0,0,0.2)',
                  border: '1px solid rgba(236,72,153,0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '13px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                  Brain Context
                </label>
                <div style={{ position: 'relative', display: 'inline-block' }} className="brain-context-help">
                  <div 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: '18px', 
                      height: '18px', 
                      borderRadius: '50%', 
                      background: 'rgba(236,72,153,0.2)',
                      cursor: 'help',
                      color: 'rgba(236,72,153,0.8)'
                    }}
                    title={`Widget: ${BRAIN_CONTEXT_HELP.widget}\n\nCase Study: ${BRAIN_CONTEXT_HELP.case_study}\n\nObserver: ${BRAIN_CONTEXT_HELP.observer}\n\nSummarizer: ${BRAIN_CONTEXT_HELP.summarizer}\n\nDocument Review: ${BRAIN_CONTEXT_HELP.document_review}`}
                  >
                    {Icons.help}
                  </div>
                </div>
              </div>
              <select 
                value={newSessionContext}
                onChange={e => setNewSessionContext(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0,0,0,0.2)',
                  border: '1px solid rgba(236,72,153,0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                <option value="widget" style={{ background: '#1a1a2e', color: '#fff' }}>Widget Conversation</option>
                <option value="case_study" style={{ background: '#1a1a2e', color: '#fff' }}>Case Study Analysis</option>
                <option value="observer" style={{ background: '#1a1a2e', color: '#fff' }}>Conversation Observer</option>
                <option value="summarizer" style={{ background: '#1a1a2e', color: '#fff' }}>Information Summarizer</option>
                <option value="document_review" style={{ background: '#1a1a2e', color: '#fff' }}>Document Review</option>
              </select>
              {/* Show description for selected context */}
              <div style={{ 
                marginTop: '8px', 
                fontSize: '11px', 
                color: 'rgba(255,255,255,0.5)',
                fontStyle: 'italic',
                lineHeight: '1.4'
              }}>
                {BRAIN_CONTEXT_HELP[newSessionContext]}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <ActionButton onClick={() => setShowNewSession(false)}>
                Cancel
              </ActionButton>
              <ActionButton primary onClick={startNewSession} disabled={!newSessionName.trim()}>
                Start Training
              </ActionButton>
            </div>
          </Card>
        </div>
      )}
    </ModuleContainer>
  );
});

export default TrainingModeModule;

